import { Content, Overlay, Portal } from '@radix-ui/react-alert-dialog';
import { isFunction } from 'lodash';
import { ReactNode, useContext } from 'react';

import { buttonVariants } from '@shared/ui/components/Button/Button.cva';
import { Icon } from '@shared/ui/components/Icon';
import { ModalContext } from '@shared/ui/components/Modal/ModalContext';
import { cn } from '@shared/utils/helpers';

type ChildOptions = {
  close: () => void;
};

type DialogContentProps = {
  title?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  withCloseButton?: boolean;
  withoutAutoFocus?: boolean;
  children: ReactNode | ((options: ChildOptions) => ReactNode);
};

export function ModalContent({
  title,
  size = 'lg',
  withCloseButton = false,
  withoutAutoFocus = false,
  children,
}: DialogContentProps) {
  const { open, setOpen } = useContext(ModalContext);

  if (!open) {
    return null;
  }

  return (
    <Portal>
      <Overlay className="fixed inset-0 z-modal bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
        <Content
          className={cn(
            'fixed left-[50%] top-[50%] overflow-hidden grid w-full translate-x-[-50%] translate-y-[-50%] gap-6 bg-white p-6 shadow-lg duration-200',
            'sm:rounded-lg',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            {
              'max-w-sm': size === 'sm',
              'max-w-md': size === 'md',
              'max-w-lg': size === 'lg',
              'max-w-xl': size === 'xl',
              'max-w-2xl': size === '2xl',
            }
          )}
          onOpenAutoFocus={(event) => {
            if (withoutAutoFocus) {
              event.preventDefault();
            }
          }}
        >
          {title && <header className="text-lg font-bold border-b -mx-6 px-6 pb-6">{title}</header>}
          {isFunction(children) ? children({ close: () => setOpen(false) }) : children}
          {withCloseButton && (
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3">
              <Icon icon="close" />
            </button>
          )}
        </Content>
      </Overlay>
    </Portal>
  );
}
